<script lang="ts">
import NavigationDrawer from "~/components/core/NavigationDrawer.vue";
import { PlatformEnum } from "~~/enums/Platform";
import Discussions from "~~/models/Discussions";
import MessagesActionDialog from "~~/models/MessagesActionDialog";
import UserModel from "~~/models/UserModel";

const router = useRouter();
const switchLocalePath = useSwitchLocalePath();
const localePath = useLocalePath();

export default {
  name: "Header",
  components: { NavigationDrawer },
  props: {
    currentProspect: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      userInformation: null as ComputedRef<UserModel> | null,
      discussions: null as ComputedRef<Discussions> | null,
      messagesDialog: new MessagesActionDialog(),
    };
  },
  watch: {
    currentProspect: {
      async handler(newValue, oldValue) {
        if (newValue) {
          //Load discussions
          this.discussions = await useProspect().getDiscussions(
            this.currentProspect?.prospectId
          );
        }
      },
    },
    discussions: {
      async handler(newValue, oldValue) {
        if (newValue) {
          if (this.hasNewMessages) {
            this.openMessages();
          }
        }
      },
    },
  },
  computed: {
    hasNewMessages() {
      return this.discussions?.discussions
        ? this.discussions.discussions.some((d) =>
            d.messages?.some(
              (m) => m.platform === PlatformEnum.PRM && !m.seenOn
            )
          )
        : false;
    },
  },
  methods: {
    async logOut() {
      await useUser().logout();
    },
    openMessages() {
      this.messagesDialog.displayDialog = true;
      this.messagesDialog.title = this.$t("messages.messagesDialogTitle");
      this.messagesDialog.prospectId = this.currentProspect?.prospectId;
    },
    changeLocale(localeCode: string) {
      let url = switchLocalePath(localeCode);
      if (!url.endsWith("/")) url = url + "/";
      navigateTo(url);
    },
    toggleDisplayRegistrations() {
      useProspect().displayRegistrations();
      // If not on page index, redirect to display the Registration section
      if (!router.currentRoute.value.name?.toString().startsWith("index")) {
        navigateTo(localePath("/") + "/");
      }
    },
  },
  async created() {
    this.userInformation = computed(() => useUser().getUserInformation());
  },
};
</script>

<template>
  <v-row class="header align-center" no-gutters>
    <v-col class="header-col-logo">
      <img
        class="header-logo"
        src="~/assets/img/logo.svg"
        :alt="$t('header.logoAlt')"
      />
    </v-col>
    <v-col class="header-application-logo">
      <img src="~/assets/img/MyCIM.svg" width="120" />
    </v-col>
    <v-col class="align-center pa-0 header-col-user">
      <div class="header-user-wrapper">
        <div v-if="!$vuetify.display.smAndDown" class="user-name">
          <!-- Username & logout button on DESKTOP -->
          <strong v-if="userInformation">{{
            userInformation.emailAddress
          }}</strong>
        </div>
        <div v-if="!$vuetify.display.smAndDown" class="user-logout">
          <v-btn
            v-if="userInformation"
            variant="plain"
            @click="logOut()"
            height="40"
          >
            <v-tooltip activator="parent" location="top">
              {{ $t("header.btnLogout") }}
            </v-tooltip>
            <v-icon size="x-large">mdi-logout</v-icon>
            <span class="btn-logout-text">
              {{ $t("header.btnLogout") }}
            </span>
          </v-btn>
        </div>
        <div class="user-messages" v-if="userInformation && currentProspect">
          <v-badge :model-value="hasNewMessages" color="error" dot>
            <v-btn variant="plain" @click="openMessages()" size="40">
              <v-tooltip
                activator="parent"
                location="top"
                :open-on-focus="false"
              >
                {{ $t("header.btnMessages") }}
              </v-tooltip>
              <v-icon size="x-large">mdi-email-outline</v-icon>
            </v-btn>
          </v-badge>
        </div>
        <div
          class="user-prospects"
          v-if="!$vuetify.display.smAndDown && userInformation"
        >
          <v-btn
            @click="toggleDisplayRegistrations()"
            variant="plain"
            size="40"
          >
            <v-tooltip activator="parent" location="top">
              {{ $t("header.btnRegistrations") }}
            </v-tooltip>
            <v-icon size="large">mdi-account-group</v-icon>
          </v-btn>
        </div>
        <div class="user-lang">
          <CoreLangSwitcher
            v-if="!$vuetify.display.smAndDown"
            class="pa-0"
            :class="$vuetify.display.smAndDown ? '' : 'ml-auto'"
            @change-locale="changeLocale($event)"
          ></CoreLangSwitcher>
          <NavigationDrawer
            v-if="$vuetify.display.smAndDown"
            :username="userInformation?.emailAddress"
            @change-locale="changeLocale($event)"
          />
        </div>
      </div>
    </v-col>
  </v-row>
  <CoreMessagesDialog :dialog="messagesDialog"></CoreMessagesDialog>
</template>

<style scoped lang="scss">
.header {
  width: 100%;
  padding-top: 25px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  flex-grow: 0;
}

.header-col-logo {
  font-size: 0;
}

.header-logo {
  width: 160px;

  @media only screen and (min-width: 960px) {
    margin-left: 20px;
  }
}

.header-application-logo {
  text-align: center;
  flex-grow: 0;
  font-size: 0;
  align-self: end;

  @media only screen and (max-width: 960px) {
  display: none;
  }

  img {
    margin: 0 20px;
  }
}

.header-col-user {
  .header-user-wrapper {
    display: flex;
    align-items: center;
    justify-content: right;
    line-height: 16px;

    .user-name {
      flex-grow: 1;
      text-align: right;
      word-break: break-all;

      padding-right: 5px;
    }

    .user-logout {
      cursor: pointer;

      button {
        min-width: 40px;
        padding: 0 10px;
      }
    }

    @media only screen and (max-width: 1280px) {
      .user-name {
        font-size: 14px;
      }

      .btn-logout-text {
        display: none;
      }
    }

    .user-messages {
      flex-grow: 0;
      cursor: pointer;

      button {
        min-width: 40px;
        padding: 0 10px;
      }
    }

    .user-prospects {
      flex-grow: 0;
      padding-right: 12px;
      cursor: pointer;

      button {
        min-width: 40px;
        padding: 0 10px;
      }
    }

    .user-lang {
      flex-grow: 0;
      line-height: 28px;

      :deep(.v-select) {
        .v-select__selection-text {
          overflow: visible;
        }
      }
    }
  }
}

.user-information {
  font-weight: 500;
  font-size: 1.1em;
  display: flex;
  align-items: center;
  margin-top: 10px;
  float: right;
  min-width: 200px;
}
.user-information > span {
  margin-left: 5px;
}
</style>
